import React, { useState, useEffect } from 'react'
import { useResidentTerms } from '../hooks/useResidentTerms'
import {
    Grid,
    Box,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Paper,
} from '@material-ui/core'
import { useTermSelectionStyles } from './styles'
import { useLeaseAuditDetail } from '../hooks/useLeaseAuditDetail'
import { TermStatusFilter } from '../types'

interface TermSelectionProps {
    auditId: number
}
export const TermSelection: React.FC<TermSelectionProps> = ({ auditId }) => {
    const classes = useTermSelectionStyles()
    const {
        leaseAudit,
        updateLeaseAudit,
        getTermStatusCounts,
        isLoadingTermStatusCounts,
        termStatusCountsError,
        termStatusCounts,
    } = useLeaseAuditDetail({ auditId })
    useEffect(() => {
        getTermStatusCounts()
    }, [])
    const [selectedTermFilter, setSelectedTermFilter] = useState(
        leaseAudit?.term_status_filter || 'all',
    )

    const handleTermFilterChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setSelectedTermFilter(event.target.value)
        updateLeaseAudit({
            term_status_filter: event.target.value as TermStatusFilter,
        })
    }

    const allTermStatusCounts =
        termStatusCounts?.past +
        termStatusCounts?.current +
        termStatusCounts?.future

    return (
        <Grid container spacing={2} className={classes.statsGrid}>
            {/* Past */}
            <Grid item xs={1}>
                <Box className={classes.statTile}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginBottom: 4 }}
                    >
                        Past
                    </Typography>
                    <Typography
                        variant="h4"
                        style={{ color: '#f59e0b', fontWeight: 600 }}
                    >
                        {termStatusCounts?.past}
                    </Typography>
                </Box>
            </Grid>
            {/* Current */}
            <Grid item xs={1}>
                <Box className={classes.statTile}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginBottom: 4 }}
                    >
                        Current
                    </Typography>
                    <Typography
                        variant="h4"
                        style={{ color: '#2196f3', fontWeight: 600 }}
                    >
                        {termStatusCounts?.current}
                    </Typography>
                </Box>
            </Grid>
            {/* Future */}
            <Grid item xs={1}>
                <Box className={classes.statTile}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginBottom: 4 }}
                    >
                        Future
                    </Typography>
                    <Typography
                        variant="h4"
                        style={{ color: '#10b981', fontWeight: 600 }}
                    >
                        {termStatusCounts?.future}
                    </Typography>
                </Box>
            </Grid>
            {/* Total */}
            <Grid item xs={1}>
                <Box className={classes.statTile}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginBottom: 4 }}
                    >
                        Total
                    </Typography>
                    <Typography variant="h4" style={{ fontWeight: 600 }}>
                        {allTermStatusCounts}
                    </Typography>
                </Box>
            </Grid>
            {/* Radio Selector for Term Status */}
            <Grid item xs={8} className={classes.termFilterWrap}>
                <Paper
                    elevation={0}
                    className={classes.statTile}
                    style={{ height: 'fit-content', minHeight: 60 }}
                >
                    <FormControl component="fieldset" style={{ width: '100%' }}>
                        <RadioGroup
                            value={selectedTermFilter}
                            onChange={handleTermFilterChange}
                            row
                            style={{ gap: 16 }}
                        >
                            <FormControlLabel
                                value="ALL_PERIODS"
                                control={
                                    <Radio
                                        color="primary"
                                        size="small"
                                        style={{ padding: 6 }}
                                    />
                                }
                                label={
                                    <Box
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 500 }}
                                        >
                                            All
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#6b7280',
                                                fontSize: '0.75rem',
                                                backgroundColor: '#f3f4f6',
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {allTermStatusCounts}
                                        </Typography>
                                    </Box>
                                }
                                style={{ margin: 0 }}
                            />
                            <FormControlLabel
                                value="CURRENT_ONLY"
                                control={
                                    <Radio
                                        color="primary"
                                        size="small"
                                        style={{ padding: 6 }}
                                    />
                                }
                                label={
                                    <Box
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 500 }}
                                        >
                                            Current Only
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#6b7280',
                                                fontSize: '0.75rem',
                                                backgroundColor: '#f3f4f6',
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {termStatusCounts?.current}
                                        </Typography>
                                    </Box>
                                }
                                style={{ margin: 0 }}
                            />
                            <FormControlLabel
                                value="CURRENT_AND_FUTURE"
                                control={
                                    <Radio
                                        color="primary"
                                        size="small"
                                        style={{ padding: 6 }}
                                    />
                                }
                                label={
                                    <Box
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 500 }}
                                        >
                                            Current & Future
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#6b7280',
                                                fontSize: '0.75rem',
                                                backgroundColor: '#f3f4f6',
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {termStatusCounts?.current +
                                                termStatusCounts?.future}
                                        </Typography>
                                    </Box>
                                }
                                style={{ margin: 0 }}
                            />
                        </RadioGroup>
                    </FormControl>
                </Paper>
            </Grid>
        </Grid>
    )
}
